import type { BaseProps } from '@/frontend/features/Fde/types'

export type TriggerStatus = 'draft' | 'active' | 'paused' | 'suspended'
export type EvaluationResult = 'running' | 'fired' | 'quiet' | 'skipped' | 'held' | 'failed'
export type EventKind = 'entered' | 'changed' | 'exited' | 'present' | 'batch'
export type DeliveryStatus = 'pending' | 'delivering' | 'delivered' | 'recorded' | 'failed' | 'dead'

export interface SourceField { key: string; field: string; label?: string; type?: string }
export interface SourceFilter {
  field: string
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'in' | 'not_in' | 'contains' | 'not_contains' | 'empty'
    | 'not_empty' | 'is_true' | 'is_false' | 'before' | 'after' | 'older_than_days' | 'within_days'
  value?: unknown
  label?: string
  type?: string
}
export interface ObjectTypeRef { id?: string; key?: string; name: string }
export interface TableSource {
  kind: 'table'; table: string; label?: string; object_type?: ObjectTypeRef | null; fields: SourceField[]; identity: string[]
  title?: string | null; filters?: SourceFilter[]; max_rows?: number
}
export interface MetricSource {
  kind: 'metric'; table: string; label?: string; object_type?: ObjectTypeRef | null
  measure: { agg: 'count' | 'count_distinct' | 'sum' | 'avg' | 'min' | 'max'; field?: string | null; label?: string }
  group_by: SourceField[]; filters?: SourceFilter[]; threshold: { op: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq'; value: number }
  max_rows?: number
}
export interface PythonSource { kind: 'python'; code: string; label?: string; output?: string; max_rows?: number; params?: Record<string, unknown>; timeout_s?: number; memory_mb?: number }
export interface SqlSource { kind: 'sql'; sql: string; label?: string; max_rows?: number }
export interface ComputationSource {
  kind: 'computation'; organization_id: string; project_id: string; publication_id: string; version: string
  data_principal_id: string; data_policy_version: string; computation: string; table: string
  filters?: Record<string, unknown>; label?: string; publication_title?: string; max_rows?: number
}
export type TriggerSource = TableSource | MetricSource | PythonSource | ComputationSource | SqlSource

export interface TriggerSpec {
  schema?: string
  source: TriggerSource
  entity: string[]
  watch?: string[]
  fire_on: Array<'entered' | 'changed' | 'exited' | 'present'>
  start?: 'now' | 'all'
  schedule?: { check_every_minutes: number }
  gate?: {
    require_sync_success?: boolean
    allow_partial_sync?: boolean
    max_staleness_hours?: number
    max_exit_share?: number
    max_events?: number
    min_rows?: number | null
  }
  target: { kind: 'none' } | { kind: 'praxis'; tenant?: string; workflow: string; batch?: boolean; input?: { static?: Record<string, unknown> } }
}

export interface ForgeTable {
  name: string; label: string; layer: string; layer_label: string; description?: string; row_count: number | null
  fields: Array<{ name: string; label: string; type: string; technical?: boolean }>
}
export interface ObjectTypeOption {
  id: string; key: string; name: string; description?: string | null; ready: boolean; reason?: string; source?: TableSource
}
export interface DashboardAnalysis {
  publication_id: string; title: string; version: string; organization_id: string; project_id: string
  data_principal_id: string; data_policy_version: string
  filters: Array<{ name: string; label?: string; type?: string; default?: unknown; options?: unknown[] }>
  computations: Array<{ name: string; tables: string[]; cards: string[] }>
}
export interface TriggerSources {
  object_types: ObjectTypeOption[]; tables: ForgeTable[]; tables_error?: string | null
  analyses?: DashboardAnalysis[]; analyses_error?: string | null; analyses_deferred?: boolean
}

export interface GateConnection {
  connection_id: string
  name: string
  sync: string | null
  sync_finished_at: string | null
  preparation: string | null
  preparation_finished_at: string | null
  health: string | null
  in_progress: boolean
}

export interface Gate {
  status?: 'open' | 'held' | 'skipped'
  reasons?: string[]
  watermark?: string | null
  connections?: GateConnection[]
}

export interface Trigger {
  id: string
  name: string
  description: string
  status: TriggerStatus
  suspended_reason: string | null
  current_revision: number
  spec: TriggerSpec
  next_evaluation_at: string | null
  created_by: string
  created_at: string
  updated_at: string
  has_secret?: boolean
  last_status?: EvaluationResult | null
  last_started_at?: string | null
  last_finished_at?: string | null
  last_gate?: Gate | null
  last_error?: string | null
  events_7d?: number
  events_dead?: number
  events_waiting?: number
  revisions?: Array<{ revision: number; note: string; created_by: string; created_at: string; spec_sha256: string }>
}

export interface Evaluation {
  id: string
  revision: number
  reason: string
  status: EvaluationResult
  gate: Gate
  watermark: string | null
  rows_matched: number | null
  entered: number
  changed: number
  exited: number
  events_created: number
  error: string | null
  started_at: string
  finished_at: string | null
}

export interface TriggerEvent {
  id: string
  kind: EventKind
  entity_key: string
  payload: Record<string, unknown>
  status: DeliveryStatus
  attempts: number
  execution_id: string | null
  execution_url: string | null
  last_error: string | null
  created_at: string
  delivered_at: string | null
  attempts_detail?: never
}

export interface Preview {
  gate: Gate
  target: string
  rows?: number
  columns?: string[]
  sample?: Array<Record<string, unknown>>
  would_fire?: { entered: number; changed: number; exited: number; events: number }
  first_run?: string
  holds?: string[]
  error?: string
  query?: string
  entity?: string[]
}

export interface TriggerAgent { worker_id: string; name: string; role: string; chat_path: string }

export interface TriggersPageProps extends BaseProps {
  triggers_copy: Record<string, string>
  trigger_agents: TriggerAgent[]
  praxis: { available: boolean; tenant?: string | null; names: string[]; error?: string }
  sources?: TriggerSources | null
  paths: { index: string; new: string; data: string; workflows: string | null; base: string }
  error?: string | null
}

export const fill = (text: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((out, [key, value]) => out.split(`{${key}}`).join(String(value)), text ?? '')

export function ago(value: string | null | undefined): string {
  if (!value) return '—'
  const seconds = (Date.now() - new Date(value).getTime()) / 1000
  if (seconds < 60) return 'agora'
  if (seconds < 3600) return `há ${Math.round(seconds / 60)} min`
  if (seconds < 86400) return `há ${Math.round(seconds / 3600)} h`
  return `há ${Math.round(seconds / 86400)} d`
}

export function when(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function entityText(key: string): string {
  if (key === '*') return 'todas'
  try {
    const values = JSON.parse(key)
    return Array.isArray(values) ? values.map((v) => (v == null ? '—' : String(v))).join(' · ') : key
  } catch {
    return key
  }
}

/** Triggers that need a person first: suspended, failing, waiting on data, or with stuck deliveries. */
export function attentionRank(t: Trigger): number {
  if (t.status === 'suspended') return 0
  if ((t.events_dead ?? 0) > 0) return 1
  if (t.status === 'active' && t.last_status === 'failed') return 2
  if (t.status === 'active' && t.last_status === 'held') return 3
  if (t.status === 'active') return 4
  if (t.status === 'draft') return 5
  return 6
}

export function needsAttention(t: Trigger): boolean {
  return attentionRank(t) <= 3
}

export function csrf(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

export async function postJson<T>(url: string, body: unknown, signal?: AbortSignal, requestKey?: string): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(),
      ...(requestKey ? { 'Idempotency-Key': requestKey } : {}) },
    body: JSON.stringify(body),
    signal,
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error((payload as { error?: string }).error || `Erro ${response.status}`)
  return payload as T
}

export async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error((payload as { error?: string }).error || `Erro ${response.status}`)
  return payload as T
}

export function withProject(path: string, projectId?: string | null, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({ ...(projectId ? { project_id: projectId } : {}), ...extra })
  const query = params.toString()
  return query ? `${path}?${query}` : path
}
